// Tiny client-side image compressor — no external libraries.
// Squashes a phone-camera photo (typically 3-8 MB) down to roughly 100-200 KB
// before it ever leaves the device. Critical for data-sensitive users on
// pay-as-you-go bundles where every 5 cents counts.
//
// - Reads the file through the browser's native <img> + <canvas> APIs.
// - Resizes so the longest edge is at most `maxEdge` px (default 1024).
// - Re-encodes as JPEG at the given quality (default 0.72 — invisible loss).
// - Returns a fresh File object the upload code can use as-is.
//
// Falls back to the original file silently on any error (e.g. unsupported
// browser, corrupt image) so activation never breaks because of compression.

const DEFAULT_MAX_EDGE = 1024;
const DEFAULT_QUALITY  = 0.72;

export async function compressImage(file, opts = {}) {
  if (!file || !(file instanceof Blob)) return file;
  // Already small enough — skip the work.
  if (file.size <= 250 * 1024) return file;

  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);

    const { width, height } = scaleFit(img.naturalWidth, img.naturalHeight, maxEdge);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // White matte for any transparent PNGs — JPEG can't store alpha.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) return file; // gave us nothing — keep original

    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function scaleFit(w, h, maxEdge) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const ratio = w >= h ? maxEdge / w : maxEdge / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}
